import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Chart from 'react-apexcharts';
import Loader from '../../component/common/Loader';
import { formatBigNumber, minsToHhMm, lastNDaysLabels } from '../SleepHub/sleepHubApi';
import { fetchBreathworkAudioDashboard } from './breathworkHubApi';
import {
  StandardButton,
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  BigStatPanel,
  ProgressList,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  AREA_FILL,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  CHART_GRID_NO_X,
  paletteAt
} from '../../component/common/designSystem';
import { SESSION_LENGTH_RAMP, RATING_RAMP } from './components/breathworkTheme';

/**
 * Breathwork → Audio Dashboard.
 *
 * Two halves that answer different questions:
 *  - the LIBRARY (from the Breathwork catalogue): how much guided audio
 *    exists, how it is rated, who authored it, and how much of it nobody
 *    ever plays;
 *  - the LISTENING (from the play ledger): when people listen, for how long,
 *    how deep engagement runs, and which tracks carry the volume.
 *
 * Content CRUD stays on /breathwork/manage, via "Manage
 * Breathworks".
 *
 * Reused by the company panel via the `fetcher` prop, with `showLibrary` off:
 * a company admin has no view of the catalogue, only of what its own people
 * played, so the library panels are replaced by reach into the population.
 */
const AudioDashboard = ({
  fetcher = fetchBreathworkAudioDashboard,
  populationLabel = null,
  showLibrary = true,
  showManageAction = true
}) => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const mostPlayed = stats?.mostPlayed || [];
  const playsTrend = stats?.playsTrend || [];
  const listenMinsTrend = stats?.listenMinsTrend || [];
  const weekdayPlays = stats?.weekdayPlays || [];
  const sessionLengths = stats?.sessionLengthDistribution || [];
  const engagement = stats?.listenerEngagement || [];
  const ratings = stats?.ratingDistribution || [];
  const authors = stats?.authorSplit || [];

  const topTenPlays = mostPlayed.reduce((sum, item) => sum + (item.plays || 0), 0);

  // Company view only: what share of the population ever played a track.
  const reachPercentage =
    stats?.totalUsers > 0 ? Math.round((stats.listeners / stats.totalUsers) * 100) : null;
  const audience = populationLabel || 'users';

  // Colour-coded per track so the bar chart and the breakdown table below read
  // as the same ranking.
  const barOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: mostPlayed.map((item, index) => paletteAt(index)),
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, barHeight: '65%', distributed: true }
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => formatBigNumber(val),
      style: { fontSize: '12px' }
    },
    legend: { show: false },
    grid: CHART_GRID_NO_X,
    xaxis: {
      categories: mostPlayed.map((item) => item.name),
      labels: { formatter: (val) => formatBigNumber(val) }
    },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} plays` } }
  };

  /**
   * Plays and listening minutes on one chart, two axes: a spike in plays that
   * isn't matched by minutes means people are sampling and dropping out, which
   * is the opposite conclusion from the plays line alone.
   */
  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line' },
    colors: [CHART_COLORS.brand, CHART_COLORS.teal],
    stroke: { curve: 'smooth', width: [2, 2], dashArray: [0, 4] },
    fill: { type: ['gradient', 'solid'], gradient: AREA_FILL.gradient },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(playsTrend.length), tickAmount: 6 },
    yaxis: [
      { seriesName: 'Plays', labels: { formatter: (val) => formatBigNumber(val) } },
      {
        seriesName: 'Listening time',
        opposite: true,
        labels: { formatter: (val) => minsToHhMm(val) }
      }
    ],
    tooltip: {
      shared: true,
      y: [
        { formatter: (val) => `${val.toLocaleString()} plays` },
        { formatter: (val) => minsToHhMm(val) }
      ]
    }
  };

  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.brand],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: weekdayPlays.map((day) => day.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} plays` } }
  };

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Breathwork Audio Dashboard | Shoorah</title>
      </Helmet>
      {loader ? <Loader /> : null}

      {showManageAction ? (
        <div className="flex justify-end mb-4">
          <StandardButton
            text="Manage Breathworks"
            onClick={() => navigate('/breathwork/manage')}
          />
        </div>
      ) : null}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        {showLibrary ? (
          <StatCard
            icon={STAT_ICONS.audioLibrary}
            title="Total Breathwork Audios"
            value={stats ? stats.totalBreathworks : '-'}
          />
        ) : (
          <StatCard
            icon={STAT_ICONS.catalog}
            title="Most Played Audio"
            value={stats?.mostPlayedBreathwork || '-'}
          />
        )}
        <StatCard
          icon={STAT_ICONS.plays}
          title="Total Plays"
          value={stats ? formatBigNumber(stats.totalPlays) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.time}
          title="Total Listening Time"
          value={stats ? minsToHhMm(stats.totalPlayTimeMins) : '-'}
        />
        {showLibrary ? (
          <StatCard
            icon={STAT_ICONS.rating}
            title="Average Rating"
            value={stats ? stats.avgRating : '-'}
          />
        ) : (
          <StatCard
            icon={STAT_ICONS.completions}
            title="Plays per Listener"
            value={stats ? stats.avgPlaysPerListener : '-'}
          />
        )}
        <StatCard
          icon={STAT_ICONS.users}
          title="Listeners"
          value={stats ? formatBigNumber(stats.listeners) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Avg Session Length"
          value={stats ? `${stats.avgSessionMins} min` : '-'}
        />
      </div>

      {/* ---------- Breakdowns ---------- */}
      <div
        className={`grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 ${
          showLibrary ? 'lg:grid-cols-4' : 'lg:grid-cols-2'
        }`}
      >
        <MiniDonutCard
          title="Session Length"
          subtitle="Minutes per play"
          data={sessionLengths}
          colors={SESSION_LENGTH_RAMP}
          unit="sessions"
        />
        <MiniDonutCard
          title="Listener Depth"
          subtitle="Plays per listener"
          data={engagement}
          colors={SESSION_LENGTH_RAMP}
          unit="listeners"
        />
        {showLibrary && (
          <>
            <MiniDonutCard
              title="Library Ratings"
              subtitle="Rated audios only"
              data={ratings}
              colors={RATING_RAMP}
              unit="audios"
            />
            <MiniDonutCard
              title="Authored By"
              subtitle="Shoorah vs expert content"
              data={authors.map((entry) => ({ label: entry.label, count: entry.count }))}
              unit="audios"
              legendLimit={2}
            />
          </>
        )}
      </div>

      {/* ---------- Trends ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel
          title="Most Played Breathwork Audios"
          subtitle={showLibrary ? 'Top 10 by total plays' : `Top 10 among your ${audience}`}
        >
          {mostPlayed.length > 0 ? (
            <Chart
              type="bar"
              height={380}
              width="100%"
              series={[{ name: 'Plays', data: mostPlayed.map((item) => item.plays) }]}
              options={barOptions}
            />
          ) : (
            <PanelEmpty>No breathwork audio activity yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Plays & Listening Time" subtitle="Per day — last 30 days">
          {playsTrend.length > 0 ? (
            <Chart
              type="line"
              height={380}
              width="100%"
              series={[
                { name: 'Plays', type: 'area', data: playsTrend },
                { name: 'Listening time', type: 'line', data: listenMinsTrend }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No plays recorded yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Reach ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {showLibrary ? (
          <BigStatPanel
            title="Library Never Played"
            subtitle="Audios with zero plays"
            value={stats ? `${stats.unplayedPercentage}%` : '-'}
            caption={
              stats
                ? `${stats.unplayedBreathworks} of ${stats.totalBreathworks} audios have never been played`
                : null
            }
          />
        ) : (
          <BigStatPanel
            title="Listening Reach"
            subtitle={`Share of ${audience} who played an audio`}
            value={reachPercentage != null ? `${reachPercentage}%` : '-'}
            caption={
              stats
                ? `${formatBigNumber(stats.listeners)} of ${formatBigNumber(
                  stats.totalUsers
                )} ${audience}`
                : null
            }
          />
        )}

        <Panel title="Plays by Weekday" subtitle="When people reach for a guided session">
          {weekdayPlays.some((day) => day.plays > 0) ? (
            <Chart
              type="bar"
              height={280}
              width="100%"
              series={[{ name: 'Plays', data: weekdayPlays.map((day) => day.plays) }]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>No plays recorded yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="How Deeply People Listen" subtitle="Listeners by total plays">
          <ProgressList items={engagement} unit="listeners" />
        </Panel>
      </div>

      {mostPlayed.length > 0 && (
        <Panel
          title="Top Audios Breakdown"
          subtitle={
            stats?.topTenSharePercentage
              ? `These ${mostPlayed.length} account for ${stats.topTenSharePercentage}% of all plays`
              : 'Plays, listening time and rating per audio'
          }
          className="mt-6"
        >
          <PanelTable
            head={
              <>
                <th className="py-2 pr-4 font-medium">#</th>
                <th className="py-2 pr-4 font-medium">Audio</th>
                <th className="py-2 pr-4 font-medium text-right">Plays</th>
                <th className="py-2 pr-4 font-medium text-right">Share of top 10</th>
                <th className="py-2 pr-4 font-medium text-right">Listening Time</th>
                <th className="py-2 font-medium text-right">
                  {showLibrary ? 'Rating' : 'Listeners'}
                </th>
              </>
            }
          >
            {mostPlayed.map((item, index) => (
              <tr key={item.id || index} className={PANEL_ROW_CLASS}>
                <td className="py-2 pr-4">
                  {/* Rank swatch ties each row to its bar in the chart above. */}
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: paletteAt(index) }}
                    />
                    {index + 1}
                  </span>
                </td>
                <td className="py-2 pr-4">{item.name}</td>
                <td className="py-2 pr-4 text-right">{item.plays.toLocaleString()}</td>
                <td className="py-2 pr-4 text-right">
                  {topTenPlays > 0 ? `${Math.round((item.plays / topTenPlays) * 100)}%` : '-'}
                </td>
                <td className="py-2 pr-4 text-right">{minsToHhMm(item.playTimeMins)}</td>
                <td className="py-2 text-right">
                  {(showLibrary ? item.rating : item.listeners) ?? '-'}
                </td>
              </tr>
            ))}
          </PanelTable>
        </Panel>
      )}
    </div>
  );
};

AudioDashboard.propTypes = {
  fetcher: PropTypes.func,
  populationLabel: PropTypes.string,
  showLibrary: PropTypes.bool,
  showManageAction: PropTypes.bool
};

export default AudioDashboard;
